import _ from 'underscore';
import $ from 'jquery';
import { BaseCard } from '@bingads-webui-campaign/overview-base-card';
import { helpers } from '@bingads-webui/deterministic-view';
import { gridCardConfig, changeEvents, AccountsMode } from '@bingads-webui-campaign/overview-cards-common';
import Content from './content';
import template from './template.pug';

let i18n;

function getMenuItems() {
  // Can be shared by multiple tiles on the same page.
  // create a new copy every time.
  const items = [];

  items.push({
    displayText: i18n.getString('Dashboard_GridCard_ToolbarDropdown_Value'),
    action: 'PerformanceMetrics',
  });

  items.push({
    displayText: i18n.getString('Dashboard_GridCard_ToolbarDropdown_Change'),
    action: 'PerformanceMetricsChange',
  });

  items.push({
    displayText: i18n.getString('Dashboard_GridCard_ToolbarDropdown_ChangePercentage'),
    action: 'PerformanceMetricsChangePercentage',
  });

  return items;
}

/* @this GridCard */
function getCardPreference() {
  const {
    columns,
    orderby,
    direction,
    directionIndex,
  } = this.child.getState();

  const { selectedMenuItem } = this.getState();

  return {
    columns,
    orderby,
    direction,
    directionIndex,
    selectedMenuItem,
  };
}

/* @this GridCard */
function navigate({
  accountLevelRout,
  campaignLevelRout,
}) {
  if (this.navigationLevel === 'account') {
    this.deps.legacyRouter.navigateToRoute(accountLevelRout);
  } else if (this.navigationLevel === 'customer') {
    this.deps.legacyRouter.navigateToRoute('accounts_performance', { accountMode: this.accountsMode });
  } else {
    const campaignId = this.deps.state.get('campaign_id');

    // eslint-disable-next-line camelcase
    this.deps.legacyRouter.navigateToRoute(campaignLevelRout, { campaign_id: campaignId });
  }
}

/* @this GridCard */
function navigateToMainGrid() {
  let accountLevelRout;
  let campaignLevelRout;

  // For now we only support this for search term and keyword.
  // Neither of them are enabled in prod.
  // We might want to do this for all types.
  switch (this.typeName) {
    case 'SearchTerm':
      accountLevelRout = 'keyword_search_term';
      campaignLevelRout = 'campaign_keyword_search_term';
      break;
    case 'Keyword':
      accountLevelRout = 'keyword';
      campaignLevelRout = 'campaign_keyword';
      break;
    case 'Campaign':
      accountLevelRout = 'campaign';
      break;
    case 'AdGroup':
      accountLevelRout = 'adgroup';
      campaignLevelRout = 'campaign_adgroup';
      break;
    default:
  }

  const {
    instrumentation, constants,
  } = this.deps;

  instrumentation.logMessage(
    [constants.LOGCATEGORY.TRACE],
    constants.LOGLEVEL.INFO,
    'grid-card',
    `${this.navigationLevel} level ${this.typeName} type view all clicked`
  );
  navigate.call(this, {
    accountLevelRout,
    campaignLevelRout,
  });
}

function getSearchTermDetail(target) {
  const $link = $(target).closest('.grid-cell--Name').find('a[data-type=SearchTerm]');
  const campaignId = $link.data('campaignid');
  const adGroupId = $link.data('adgroupid');
  const text = $link.data('text').toString();
  const navigationLevel = $link.data('navigationlevel');
  const campaignName = $link.data('campaignname');
  const adGroupName = $link.data('adgoupname');
  const matchType = $link.data('matchtype');

  return {
    campaignId,
    adGroupId,
    text,
    navigationLevel,
    campaignName,
    adGroupName,
    matchType,
  };
}

/* @this GridCard */
function setupMenuItems() {
  const { dateRange } = this.getState();

  if (dateRange && dateRange.compared && this.typeName !== 'SearchTerm') {
    const { selectedMenuItem } = this.getState();
    const items = getMenuItems();

    _.each(items, (item) => {
      if (item.action === selectedMenuItem) {
        item.preset = true; // eslint-disable-line no-param-reassign
      } else {
        item.preset = false; // eslint-disable-line no-param-reassign
      }
    });

    this.setState({
      menuItems: items,
    });
  } else {
    this.setState({
      menuItems: null,
    });
    this.onMenuAction(gridCardConfig.defaultOptions.selectedMenuItem);
  }
}

export class GridCard extends BaseCard {
  get className() {
    return 'grid-card';
  }

  get template() {
    return () => template({
      i18n,
      buttonText: this.buttonText,
      typeName: this.typeName,
      isCustomerLevel: this.navigationLevel === 'customer',
    });
  }

  get menuItems() {
    return this.getState().menuItems;
  }

  onMenuAction(body) {
    helpers.updateView(this.child, _.defaults({
      body,
    }, this.getState()));

    if (this.menuItems) {
      const { selectedMenuItem } = this.getState();

      if (body === selectedMenuItem) {
        return;
      }

      this.setState({
        selectedMenuItem: body,
      });

      this.trigger('storeCardPreference', getCardPreference.call(this));

      const {
        instrumentation, constants,
      } = this.deps;

      instrumentation.logMessage(
        [constants.LOGCATEGORY.TRACE],
        constants.LOGLEVEL.INFO,
        'grid-card',
        `${this.navigationLevel} level ${this.typeName} type new pop type picked.`
      );
      _.defer(() => {
        // If the menu is open close it.
        this.$el.closest('.card-holder').find('.multilevel-dropdown.open').click();
      });
    }
  }

  initialize({
    entitySet,
    navigationLevel,
    columns,
    orderby,
    direction,
    directionIndex,
    selectedMenuItem,
    dateRange,
    filter,
    sharedOptions,
    fetchSingleTile = false,
    preexecuteFlag = false,
    deps,
    accountsMode = AccountsMode.Expert,
  }) {
    this.typeName = entitySet.type.elementType.shortName;
    this.navigationLevel = navigationLevel;
    this.accountsMode = accountsMode;
    this.deps = deps;
    i18n = this.deps.i18n; // eslint-disable-line

    const isSmartShoppingCampaign = this.isSmartShoppingCampaign();
    switch (this.typeName) {
      case 'SearchTerm':
        this.buttonText = !isSmartShoppingCampaign ? i18n.getString('Dashboard_TopSearchTerms_View_All') : null;
        break;
      case 'Keyword':
        this.buttonText = !isSmartShoppingCampaign ? i18n.getString('Dashboard_TopKeywords_View_All') : null;
        break;
      case 'Campaign':
        this.buttonText = i18n.getString('Dashboard_TopCampaigns_View_All');
        break;
      case 'AdGroup':
        this.buttonText = i18n.getString('Dashboard_TopAdGroups_View_All');
        break;
      case 'Account':
        this.buttonText = i18n.getString('Dashboard_TopAccounts_View_All');
        break;
      default:
        this.buttonText = undefined;
    }

    let adjustedMenuItem = selectedMenuItem;

    if (!dateRange.compared || this.typeName === 'SearchTerm') {
      adjustedMenuItem = gridCardConfig.defaultOptions.selectedMenuItem;
    }

    this.setState(_.defaults({
      selectedMenuItem: adjustedMenuItem,
      dateRange,
    }, {
      selectedMenuItem: gridCardConfig.defaultOptions.selectedMenuItem,
    }));

    this.child = new Content(_.extend({
      entitySet,
      navigationLevel,
      parent: this,
      columns,
      orderby,
      direction,
      directionIndex,
      dateRange,
      filter,
      fetchSingleTile,
      preexecuteFlag,
      accountsMode,
      getCardsConfig: sharedOptions.getCardsConfig,
      deps: this.deps,
      typeId: this.options.typeId,
    }, _.pick(this.getState(), 'selectedMenuItem')));

    this.setChild('content', this.child);

    setupMenuItems.call(this);
    helpers.updateView(this.child, this.getState());

    this.listenTo(this.model, changeEvents('dateRange', 'filter'), () => {
      helpers.updateView(this.child, this.getState());
    });

    this.listenTo(this.model, changeEvents('size'), () => {
      if (this.addKeywordView && this.$el.closest('.dashboard').find(`.${this.addKeywordView.className}`).length > 0) {
        this.addKeywordView.updatePosition();
      }
      this.child.setupScrollBarCover();
    });

    this.on('rendered', () => {
      this.child.setupScrollBarCover();
    });

    this.listenTo(this.model, changeEvents('dateRange'), () => {
      setupMenuItems.call(this);
    });

    this.listenTo(this.model, changeEvents('menuItems'), () => {
      this.trigger('change:menuItems', {
        value: this.getState().menuItems,
        options: { replace: true },
      });
    });

    this.onResize(() => {
      this.child.setupScrollBarCover();
    });

    this.child.on('gridOptionChange', () => this.trigger('storeCardPreference', getCardPreference.call(this)));
  }

  reloadGrid() {
    this.child.gridView.refresh(true);
  }

  get events() {
    return {
      'click .view-all-clickLink': () => navigateToMainGrid.call(this),
      'click button.add': this.addAsKeyword,
      'click button.exclude': this.addAsNegativeKeyword,
    };
  }

  addAsKeyword(event) {
    /* eslint-disable global-require */
    import(/* webpackMode: 'eager' */'./search-term-operations/add-keyword').then(({ AddKeywordView }) => {
      this.addKeywordView = new AddKeywordView(_.extend(getSearchTermDetail(event.currentTarget), {
        gridCard: this,
        addAsNegativeKeyword: false,
        reloadGrid: () => this.reloadGrid(),
        deps: this.deps,
      })).render();

      $(event.currentTarget).closest('.dashboard').append(this.addKeywordView.$el);
    });
  }

  addAsNegativeKeyword(event) {
    /* eslint-disable global-require */
    import(/* webpackMode: 'eager' */'./search-term-operations/add-keyword').then(({ AddKeywordView }) => {
      this.addKeywordView = new AddKeywordView(_.extend(getSearchTermDetail(event.currentTarget), {
        gridCard: this,
        addAsNegativeKeyword: true,
        reloadGrid: () => this.reloadGrid(),
        deps: this.deps,
      })).render();

      $(event.currentTarget).closest('.dashboard').append(this.addKeywordView.$el);
    });
  }
}
